import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, KeyRound, Eye, EyeOff, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Dialog "Quên mật khẩu" — 2 bước:
 * 1) Nhập email → gửi OTP qua Zalo ZNS đến SĐT đã đăng ký.
 * 2) Nhập OTP + mật khẩu mới → reset.
 */
export default function ForgotPasswordDialog({ open, onOpenChange }) {
  const [step, setStep] = useState("email"); // email | otp
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep("email"); setEmail(""); setOtp("");
    setNewPassword(""); setConfirmPassword("");
    setMaskedPhone(""); setShowPw(false); setSubmitting(false);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (!email.includes("@")) {
      toast.error("Email không hợp lệ");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password/send-otp", { email: email.trim().toLowerCase() });
      setMaskedPhone(data.masked_phone || "");
      setStep("otp");
      toast.success(data.message || "Đã gửi OTP qua Zalo");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gửi OTP thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async (e) => {
    e?.preventDefault();
    if (otp.length !== 6) {
      toast.error("Mã OTP phải có 6 chữ số");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Mật khẩu mới tối thiểu 6 ký tự");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Mật khẩu xác nhận không khớp");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password/reset", {
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
        new_password: newPassword,
      });
      toast.success("Đã đặt lại mật khẩu thành công. Vui lòng đăng nhập với mật khẩu mới.");
      handleClose(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Đặt lại mật khẩu thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-[#0f172a] border-[#334155]" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-50 flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-cyan-400" />
            Quên mật khẩu
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {step === "email"
              ? "Nhập email của tài khoản — hệ thống sẽ gửi mã OTP qua Zalo đến SĐT đã đăng ký."
              : `Mã OTP 6 chữ số đã được gửi tới ${maskedPhone}. Mã có hiệu lực 10 phút.`}
          </DialogDescription>
        </DialogHeader>

        {step === "email" ? (
          <form onSubmit={handleSendOtp} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email" placeholder="you@example.com" value={email}
                  onChange={(e) => setEmail(e.target.value)} required autoFocus
                  className="pl-10 bg-[#1e293b] border-[#334155] text-slate-50 h-11"
                  data-testid="forgot-password-email-input"
                />
              </div>
            </div>
            <Button type="submit" disabled={submitting}
              className="w-full bg-cyan-500 text-[#0f172a] font-semibold hover:bg-cyan-400 h-11"
              data-testid="forgot-password-send-otp-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gửi mã OTP qua Zalo"}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleReset} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Mã OTP (6 chữ số)</Label>
              <Input
                type="text" inputMode="numeric" maxLength={6}
                placeholder="123456" value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                required autoFocus
                className="bg-[#1e293b] border-[#334155] text-slate-50 h-11 tracking-widest text-center font-mono text-lg"
                data-testid="forgot-password-otp-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Mật khẩu mới</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type={showPw ? "text" : "password"} placeholder="Tối thiểu 6 ký tự"
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  required minLength={6}
                  className="pl-10 pr-10 bg-[#1e293b] border-[#334155] text-slate-50 h-11"
                  data-testid="forgot-password-new-password-input"
                />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Xác nhận mật khẩu</Label>
              <Input
                type={showPw ? "text" : "password"} placeholder="Nhập lại mật khẩu mới"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                className="bg-[#1e293b] border-[#334155] text-slate-50 h-11"
                data-testid="forgot-password-confirm-password-input"
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setStep("email")} disabled={submitting}
                className="flex-1 border-[#334155] text-slate-300 hover:bg-[#1e293b] h-11"
                data-testid="forgot-password-back-btn">
                ← Quay lại
              </Button>
              <Button type="submit" disabled={submitting}
                className="flex-1 bg-cyan-500 text-[#0f172a] font-semibold hover:bg-cyan-400 h-11"
                data-testid="forgot-password-reset-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Đặt lại mật khẩu"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
